'use client';

import type * as CANNON from 'cannon-es';
import { useCallback, useRef } from 'react';
import * as THREE from 'three';

import type {
  GameConfig,
  Layer,
} from '@/app/(football)/[team]/mini-game/_components/Section/GameSection/BlockStackingGame/types';

const BOX_HEIGHT = 1;
const ORIGINAL_BOX_SIZE = 3;

interface GameLogicDeps {
  createPhysicsBody: (
    x: number,
    y: number,
    z: number,
    width: number,
    depth: number,
    boxHeight: number,
    falls: boolean,
  ) => CANNON.Body;
  updatePhysicsBodyShape: (body: CANNON.Body, width: number, height: number, depth: number) => void;
  addMeshToScene: (mesh: THREE.Mesh) => void;
  removeMeshFromScene: (mesh: THREE.Mesh) => void;
  removePhysicsBody: (body: CANNON.Body) => void;
  gameConfig: GameConfig;
}

/**
 * Game logic hook
 * Manages core game logic including block creation, cutting, and overhang handling
 */
export function useGameLogic(deps: GameLogicDeps) {
  const stackRef = useRef<Layer[]>([]);
  const overhangsRef = useRef<Layer[]>([]);

  const createMesh = useCallback(
    (x: number, y: number, z: number, width: number, depth: number, colorIndex?: number) => {
      const geometry = new THREE.BoxGeometry(width, BOX_HEIGHT, depth);

      const actualColorIndex =
        colorIndex !== undefined
          ? colorIndex % deps.gameConfig.colors.length
          : stackRef.current.length % deps.gameConfig.colors.length;
      const colorHex = deps.gameConfig.colors[actualColorIndex];
      const color = new THREE.Color(colorHex);

      const material = new THREE.MeshLambertMaterial({ color });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(x, y, z);
      return mesh;
    },
    [deps.gameConfig.colors],
  );

  const generateBox = useCallback(
    (x: number, y: number, z: number, width: number, depth: number, falls: boolean, colorIndex?: number): Layer => {
      const mesh = createMesh(x, y, z, width, depth, colorIndex);
      deps.addMeshToScene(mesh);

      const body = deps.createPhysicsBody(x, y, z, width, depth, BOX_HEIGHT, falls);

      return {
        threejs: mesh,
        cannonjs: body,
        width,
        depth,
        direction: undefined,
      };
    },
    [createMesh, deps],
  );

  const addLayer = useCallback(
    (x: number, z: number, width: number, depth: number, direction?: 'x' | 'z') => {
      const y = BOX_HEIGHT * stackRef.current.length;
      const layer = generateBox(x, y, z, width, depth, false);
      layer.direction = direction;
      stackRef.current.push(layer);
    },
    [generateBox],
  );

  const addOverhang = useCallback(
    (x: number, z: number, width: number, depth: number, currentBlockColorIndex: number) => {
      const y = BOX_HEIGHT * (stackRef.current.length - 1);
      const overhang = generateBox(x, y, z, width, depth, true, currentBlockColorIndex);
      overhangsRef.current.push(overhang);
    },
    [generateBox],
  );

  const cutBox = useCallback(
    (topLayer: Layer, overlap: number, size: number, delta: number) => {
      const direction = topLayer.direction;
      if (!direction) return;

      const newWidth = direction === 'x' ? overlap : topLayer.width;
      const newDepth = direction === 'z' ? overlap : topLayer.depth;

      topLayer.width = newWidth;
      topLayer.depth = newDepth;

      topLayer.threejs.scale[direction] = overlap / size;
      topLayer.threejs.position[direction] -= delta / 2;

      topLayer.cannonjs.position[direction] -= delta / 2;

      deps.updatePhysicsBodyShape(topLayer.cannonjs, newWidth, BOX_HEIGHT, newDepth);
    },
    [deps],
  );

  const missedTheSpot = useCallback(
    (onGameEnd: () => void) => {
      const topLayer = stackRef.current[stackRef.current.length - 1];
      if (!topLayer) return;

      const currentBlockColorIndex = (stackRef.current.length - 1) % deps.gameConfig.colors.length;

      addOverhang(
        topLayer.threejs.position.x,
        topLayer.threejs.position.z,
        topLayer.width,
        topLayer.depth,
        currentBlockColorIndex,
      );
      deps.removePhysicsBody(topLayer.cannonjs);
      deps.removeMeshFromScene(topLayer.threejs);

      onGameEnd();
    },
    [addOverhang, deps],
  );

  const splitBlockAndAddNextOneIfOverlaps = useCallback(
    (onScoreUpdate: (score: number) => void, onMissed: () => void) => {
      const topLayer = stackRef.current[stackRef.current.length - 1];
      const previousLayer = stackRef.current[stackRef.current.length - 2];

      if (!topLayer?.direction || !previousLayer) return;

      const direction = topLayer.direction;
      const size = direction === 'x' ? topLayer.width : topLayer.depth;
      const delta = topLayer.threejs.position[direction] - previousLayer.threejs.position[direction];
      const overhangSize = Math.abs(delta);
      const overlap = size - overhangSize;

      if (overlap > 0) {
        const currentBlockColorIndex = (stackRef.current.length - 1) % deps.gameConfig.colors.length;

        cutBox(topLayer, overlap, size, delta);

        const overhangShift = (overlap / 2 + overhangSize / 2) * Math.sign(delta);
        const overhangX = direction === 'x' ? topLayer.threejs.position.x + overhangShift : topLayer.threejs.position.x;
        const overhangZ = direction === 'z' ? topLayer.threejs.position.z + overhangShift : topLayer.threejs.position.z;
        const overhangWidth = direction === 'x' ? overhangSize : topLayer.width;
        const overhangDepth = direction === 'z' ? overhangSize : topLayer.depth;

        addOverhang(overhangX, overhangZ, overhangWidth, overhangDepth, currentBlockColorIndex);

        const nextX = direction === 'x' ? topLayer.threejs.position.x : -10;
        const nextZ = direction === 'z' ? topLayer.threejs.position.z : -10;
        const newWidth = topLayer.width;
        const newDepth = topLayer.depth;
        const nextDirection = direction === 'x' ? 'z' : 'x';

        onScoreUpdate(stackRef.current.length - 1);
        addLayer(nextX, nextZ, newWidth, newDepth, nextDirection);
      } else {
        onMissed();
      }
    },
    [cutBox, addOverhang, addLayer, deps.gameConfig.colors.length],
  );

  const resetGameLogic = useCallback(() => {
    stackRef.current = [];
    overhangsRef.current = [];
  }, []);

  const setupInitialBlocks = useCallback(() => {
    addLayer(0, 0, ORIGINAL_BOX_SIZE, ORIGINAL_BOX_SIZE);
    addLayer(-10, 0, ORIGINAL_BOX_SIZE, ORIGINAL_BOX_SIZE, 'x');
  }, [addLayer]);

  return {
    stackRef,
    overhangsRef,
    addLayer,
    addOverhang,
    generateBox,
    cutBox,
    missedTheSpot,
    splitBlockAndAddNextOneIfOverlaps,
    resetGameLogic,
    setupInitialBlocks,
    constants: { BOX_HEIGHT, ORIGINAL_BOX_SIZE },
  };
}
